import {configureStore, combineReducers, nanoid, MiddlewareArray} from '@reduxjs/toolkit';
import projectReducer from './project-slice';
import foldersReducer from './folders-slice';
import assetsReducer from './assets-slice';
import projectionsReducer from './projections-slice';
import settingsReducer from './settings-slice';
import cameraReducer from './camera-slice';
import bookmarksReducer, { Bookmark } from './bookmarks-slice';
import customLinksReducer from './custom-links-slice';
import thunk from 'redux-thunk';
import {
  isMobile,
  getModifiedSceneState,
  getModifiedAerialState,
  toBoolean
} from '../viewer/js/utilities';
import { useDispatch } from 'react-redux';

const appReducer = combineReducers({
  project: projectReducer,
  camera: cameraReducer,
  folders: foldersReducer,
  projections: projectionsReducer,
  settings: settingsReducer,
  assets: assetsReducer,
  bookmarks: bookmarksReducer,
  custom_links: customLinksReducer
});

const updateStateFromParameters = (state, parameters) => {
  if (!parameters) return state;

  const {sceneState, aerialState} = state.camera;

  state.camera.sceneState = getModifiedSceneState(
    sceneState, parameters);

  state.camera.aerialState = getModifiedAerialState(
    aerialState, parameters);

  if ("expanded" in parameters) {
    const switched = toBoolean(parameters.expanded);
    state.settings.switched = switched;
  }

  return state;
};

const updateStateFromBookmark = (state, parameters) => {
  if (!parameters) return state;

  if ("bookmark" in parameters) {
    const bookmarkID = parameters.bookmark;
    const bookmark = state.bookmarks.find(bookmark => {
      return bookmark.id === bookmarkID;
    }) as Bookmark;

    if (!bookmark) return state;

    state.settings = bookmark.settings;
    state.camera.aerialState = bookmark.aerialState;
    state.camera.sceneState = bookmark.sceneState;

    state.assets.forEach(asset => {
      asset.visible = bookmark.visibleAssets.includes(asset.id);
    });
  }

  return state;
};

const updateStateFromTag = (state, parameters) => {
  if (!parameters) return state;

  if ("tag" in parameters) {
    const savedTagID = parameters.tag;
    state.camera.forcedTagID = savedTagID;
  }

  return state;
};

const rootReducer = (state, action) => {
  const newProject = action.type === 'NEW';
  const loadConfig = action.type === 'LOAD';
  const configInit = state === undefined;

  if (newProject) {
    state = undefined;
  } else if (loadConfig) {
    const {projectJSON} = action.payload;
    state = projectJSON;
  }

  state = appReducer(state, action);

  if (loadConfig) {
    // Modify project state based on URL parameters
    const {parameters} = action.payload;
    state = updateStateFromParameters(state, parameters);
    state = updateStateFromBookmark(state, parameters);
    state = updateStateFromTag(state, parameters);
  }

  if (configInit || newProject) {
    state = appReducer(state, {
      type: "settings/applySavedDefaults",
      payload: null
    });
  }

  if (newProject) {
    /** Create unique project ID */
    state = appReducer(state, {
      type: "project/updateProjectID",
      payload: nanoid()
    });
  }

  if (newProject || loadConfig) {
    /** Update unique session ID */
    state = appReducer(state, {
      type: "project/updateSessionID",
      payload: nanoid()
    });
  }

  // Asset drawer will be forced to closed
  // for mobile devices on initial load
  if (loadConfig && isMobile) {
    state = appReducer(state, {
      type: "settings/changeDrawerOpen",
      payload: false
    });
  }

  if (loadConfig) {
    appReducer(state, {
      type: "settings/changeDefaultSettings",
      payload: null
    });
  }

  return state;
};

const middleware = new MiddlewareArray()
  .concat(thunk);

export const store = configureStore({
  reducer: rootReducer,
  middleware
});

export type AppDispatch = typeof store.dispatch;

/** Custom useDispatch with proper typing for our store */
export const useAppDispatch: () => AppDispatch = useDispatch;